import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useLazyQuery } from '@apollo/react-hooks';
import { get } from 'lodash';

import { useMediaQuery, createBreakpoints } from 'WeployDesignLanguage/mediaQueries';
import EmptyState from 'WeployDesignLanguage/EmptyState';

import { SlidingModal } from 'LegacyWeployDesignLanguage/Modal';
import Grid from 'LegacyWeployDesignLanguage/Grid';
import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';

import NoResultsImage from 'images/weployer/no-results.svg';

import InfiniteScroll from '../../../containers/weployer/InfiniteScroll';
import { getExternalPath, PROFILE_HIRE_PATH } from '../../../containers/weployer/pathHelpers';
import { navigateExternal } from '../../../containers/weployer/common/navigation';
import Filters from '../../../components/weployer/search/Filters';
import WeployeeProfileSummary from '../../../components/weployer/search/WeployeeProfileSummary';
import { WeployeeProfile } from '../../../components/shortlistedWeployees/WeployeeProfile';
import {
  SEARCH_PERFORM,
  SEARCH_PROFILE_SUMMARY_CLICK,
  SEARCH_SEND_JOB_CLICK,
  SEARCH,
  trackEvent,
  trackPageView,
  WEPLOYER,
} from '../../../utils/analytics';

import { GET_WEPLOYEE_PROFILE_SUMMARIES } from './graphql/queries';
import {
  availabilitySchedule,
  availabilities,
  workExperiences,
  weployeeDetails,
  stripInitials,
  mapLocation,
} from './mappers';
import {
  ALL_LOCATIONS,
  FULL_TIME_OR_PART_TIME,
  LOCATION_OPTIONS,
  AVAILABILITY_OPTIONS,
} from './constants';

const PAGE_SIZE = 20;
const DRAWER_WIDTH = 700;
const DRAWER_WIDTH_IN_PX = `${DRAWER_WIDTH}px`;
const colors = WeployeeProfileSummary.COLORS;

const StyledWeployeeProfileSummaryWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing[2]};
`;

const StyledSlidingModal = styled(SlidingModal)`
  max-width: ${DRAWER_WIDTH_IN_PX};

  ${props => props.isMobile && css`
    width: 100%;
  `}
`;

const StyledSearchResultsContainer = styled.div`
  margin-left: ${props => props.theme.spacing[2]};
  width: 100%;

  ${props => props.drawerOpen && props.isWideDisplay && css`
    width: ${`${props.containerWidth - DRAWER_WIDTH}px`};
    transition: width 0.25s linear;
  `};
`;

const SearchResults = memo(({
  onSummaryClick,
  weployeeIds,
  weployees,
}) => (
  <>
    { weployeeIds.length > 0 ?
      weployeeIds.map((weployeeId, index) => {
        const {
          activePositions,
          availabilities: availabilitiesList,
          initials,
          state,
          systemKnowledges: systemsKnowledges,
          technicalSkills: skills,
          userId: id,
          workExperiences: workExperiencesList,
        } = weployees[weployeeId];

        return (
          <StyledWeployeeProfileSummaryWrapper key={id}>
            <WeployeeProfileSummary
              availabilities={availabilities(availabilitiesList)}
              availabilitySchedule={availabilitySchedule(activePositions)}
              avatarColour={colors[index % colors.length]}
              id={id}
              initials={stripInitials(initials)}
              onClick={onSummaryClick}
              skills={skills}
              state={state}
              systemsKnowledges={systemsKnowledges}
              workExperiences={workExperiences(workExperiencesList)}
            />
          </StyledWeployeeProfileSummaryWrapper>
        );
      })
      : (
        <EmptyState
          iconSize={EmptyState.ICON_LARGE}
          icon={<img src={NoResultsImage} alt="Profile hire" />}
          title="No results matched"
          description="Try changing your search criteria"
        />
      )
    }
  </>
));

SearchResults.propTypes = {
  onSummaryClick: PropTypes.func.isRequired,
  weployeeIds: PropTypes.array,
  weployees: PropTypes.object,
};

SearchResults.defaultProps = {
  weployeeIds: [],
  weployees: {},
};

const Search = () => {
  const [locationFilter, setLocationFilter] = useState(ALL_LOCATIONS);
  const [availabilityFilter, setAvailabilityFilter] = useState(FULL_TIME_OR_PART_TIME);
  const [skillsFilter, setSkillsFilter] = useState([]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [openWeployeeDetails, setOpenWeployeeDetails] = useState(false);
  const [weployees, setWeployees] = useState({});
  const [weployeeIds, setWeployeeIds] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [fetch, { loading, data }] = useLazyQuery(GET_WEPLOYEE_PROFILE_SUMMARIES, {
    fetchPolicy: 'no-cache',
  });

  const [searchContainerWidth, setSearchContainerWidth] = useState('100%');
  const searchContainerRef = React.createRef();

  const { UP_XL, DOWN_SM } = createBreakpoints();
  const isWideDisplay = useMediaQuery(UP_XL);
  const isMobile = useMediaQuery(DOWN_SM);

  const searchCriteria = {
    variables: {
      searchTerm,
      filter: {
        availability: availabilityFilter,
        skills: skillsFilter,
        state: mapLocation(locationFilter),
      },
      first: PAGE_SIZE,
      after: currentOffset,
    },
  };

  useEffect(() => {
    trackPageView(WEPLOYER, SEARCH);
    fetch(searchCriteria);
  }, []);

  useEffect(() => {
    trackEvent(SEARCH_PERFORM, searchCriteria);
    fetch(searchCriteria);
  }, [searchTerm, availabilityFilter, skillsFilter, locationFilter, currentOffset]);

  const setWeployeeData = (weployeeData, ids) => {
    setWeployees(weployeeData);
    setWeployeeIds(ids);
  };

  const appendWeployeeData = (weployeeData, ids) => {
    setWeployees({ ...weployees, ...weployeeData });
    setWeployeeIds([...weployeeIds, ...ids]);
  };

  const isFirstPage = () => (currentOffset === 0);

  useEffect(() => {
    const weployeeProfileSummaries = get(data, 'weployer.weployeeProfileSummaries.summaries');
    const total = get(data, 'weployer.weployeeProfileSummaries.total');

    if (weployeeProfileSummaries && !loading) {
      const ids = [];
      const weployeeData = {};

      weployeeProfileSummaries.forEach((weployeeProfile) => {
        const parsedData = JSON.parse(weployeeProfile.summary);
        const { userId } = parsedData;
        weployeeData[userId] = parsedData;
        ids.push(userId);
      });

      if (isFirstPage()) {
        setWeployeeData(weployeeData, ids);
      } else {
        appendWeployeeData(weployeeData, ids);
      }
      setTotalResults(total);
    }
  }, [data]);

  useEffect(() => {
    setSearchContainerWidth(searchContainerRef.current.offsetWidth);
  }, []);

  const handleOpenWeployeeDetails = (weployeeId, avatarColor) => {
    const mappedWeployeeDetails = weployeeDetails(weployees[weployeeId]);
    setOpenWeployeeDetails({ ...mappedWeployeeDetails, avatarColor });
    trackEvent(SEARCH_PROFILE_SUMMARY_CLICK);
  };

  const handleJobButtonClick = async () => {
    trackEvent(SEARCH_SEND_JOB_CLICK);
    const profileHirePath = getExternalPath({
      pathName: PROFILE_HIRE_PATH,
      params: { 'weployee_ids[]': openWeployeeDetails.id },
    });
    navigateExternal(profileHirePath);
  };

  const resetPagination = () => setCurrentOffset(0);

  const handleSearchTermSubmit = (term) => {
    setSearchTerm(term);
    resetPagination();
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    resetPagination();
  };

  const handleLocationSelect = (value) => {
    setLocationFilter(value);
    resetPagination();
  };

  const handleSkillsChange = (skills) => {
    setSkillsFilter(skills);
    resetPagination();
  };

  const handleAvailabilityChange = (availability) => {
    setAvailabilityFilter(availability);
    resetPagination();
  };

  const handleBottomReached = () => {
    if (weployeeIds.length < totalResults && !loading) {
      setCurrentOffset(currentOffset + PAGE_SIZE);
    }
  };

  return (
    <>
      <Grid container>
        <Grid item sm={4} md={2}>
          <Filters
            collapsed={isMobile}
            currentAvailability={availabilityFilter}
            availabilityOptions={AVAILABILITY_OPTIONS}
            currentLocation={locationFilter}
            currentSkills={skillsFilter}
            locationOptions={LOCATION_OPTIONS}
            onAvailabilitySelect={handleAvailabilityChange}
            onLocationSelect={handleLocationSelect}
            onSkillsChange={handleSkillsChange}
            onSearchTermClear={handleSearchClear}
            onSearchTermSubmit={handleSearchTermSubmit}
          />
        </Grid>
        <Grid item sm={8} md={10}>
          <StyledSearchResultsContainer
            drawerOpen={openWeployeeDetails}
            isWideDisplay={isWideDisplay}
            ref={searchContainerRef}
            containerWidth={searchContainerWidth}
          >
            <Loader loading={loading && isFirstPage()} text="Loading...">
              <InfiniteScroll
                onBottomReached={handleBottomReached}
                isEndReached={false}
                height={100}
                triggerThreshold={200}
              >
                <SearchResults
                  weployees={weployees}
                  weployeeIds={weployeeIds}
                  onSummaryClick={handleOpenWeployeeDetails}
                />
              </InfiniteScroll>
            </Loader>
          </StyledSearchResultsContainer>
        </Grid>
      </Grid>
      <StyledSlidingModal
        variant={isWideDisplay ? SlidingModal.PERSISTENT : SlidingModal.TEMPORARY}
        open={!!openWeployeeDetails}
        isMobile={isMobile}
        onClose={() => setOpenWeployeeDetails(false)}
      >
        <WeployeeProfile
          onCloseButtonClick={() => setOpenWeployeeDetails(false)}
          canSendProfileHireJob
          onSendJobButtonClick={handleJobButtonClick}
          {...openWeployeeDetails}
        />
      </StyledSlidingModal>
    </>
  );
};

export default Search;
